'use client';

import { useState } from 'react';

import { USStateObject } from '@domain/states/types';
import { Heading } from '../Heading/Heading';
import Select from 'react-select';

import { StateLinks } from './components/StateLinks';
import { IAllStateWaterData } from './helpers/buildStateDataWaters';
import { SearchIcon } from '../Icons/SearchIcon';
import { Container } from '../Container/Container';
import { NextImageWrapper } from '../NextImage/NextImageWrapper';

interface IProps {
  stateWaterData: IAllStateWaterData;
  defaultUSState: USStateObject;
  usStates: USStateObject[];
}

// Handle selection change
type StateOption = {
  value: string;
  label: string;
  state: USStateObject;
};

export const FindUSFishingSpot = ({ stateWaterData, usStates, defaultUSState }: IProps) => {
  const [selectedState, setSelectedState] = useState<USStateObject>(defaultUSState);
  const ariaImageLabel = `${selectedState.stateName} silhouette`;

  const stateOptions = usStates.map(state => ({
    value: state.stateID,
    label: state.stateName,
    state: state,
  }));

  const handleChange = (selectedOption: StateOption | null) => {
    if (selectedOption) {
      setSelectedState(selectedOption.state);
    } else {
      setSelectedState(defaultUSState);
    }
  };

  return (
    <Container
      data-testid="FindUSFishingSpot"
      className="mb-sm flex max-w-[900px] flex-col items-center gap-4 sm:mx-auto sm:w-2/3 sm:px-0 xl:max-w-[900px]"
    >
      <Heading variant="sm" as="h2" className="text-center">
        Find great fishing spots near you
      </Heading>

      <Select
        instanceId="us-state-select"
        isClearable={true}
        className="bg-sea-foam w-full md:w-11/12"
        classNames={{
          control: state =>
            `p-4 sm:p-2 !border-2 !rounded-lg ${
              state.isFocused ? '!shadow-md !border-tuna' : '!shadow-none !border-twilight'
            }`,
        }}
        options={stateOptions}
        onChange={handleChange}
        placeholder={
          <div className="flex items-center gap-2">
            <SearchIcon />
            Search for US State...
          </div>
        }
      />

      <div className="flex size-full flex-col sm:flex-row sm:gap-4">
        <div className="relative mt-1 flex w-full items-center justify-center sm:mt-0 sm:w-1/2">
          <NextImageWrapper
            wrapperClassName="size-full pb-[100%]"
            imageClassName="aspect-square size-full rounded-t-xl object-cover"
            sizes="(min-width: 600px) 50vw, 100vw"
            src={selectedState.svgGray.src}
            alt={selectedState.stateName}
            fill
            showPlaceholder={false}
            aria-label={ariaImageLabel}
          />
        </div>
        {usStates.map(state => {
          const stateWatersList = stateWaterData.allStateWaterData.find(
            stateDataObj => stateDataObj.stateID === state.stateID,
          );
          return (
            <StateLinks
              selectedState={state}
              stateWatersList={stateWatersList}
              className={state.stateID === selectedState.stateID ? 'flex' : 'hidden'}
              key={`FindUSFishingSpot-${state.stateID}`}
            />
          );
        })}
      </div>
    </Container>
  );
};
